import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { formatDate, formatTimeOnly } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';

interface MetricsChartProps {
  title: string;
  data: any[];
  lines: {
    dataKey: string;
    name: string;
    color: string;
  }[];
  loading?: boolean;
  unit?: string;
}

export const MetricsChart: React.FC<MetricsChartProps> = ({
  title,
  data,
  lines,
  loading = false,
  unit = ''
}) => {
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const [legendExpanded, setLegendExpanded] = useState(lines.length <= 4);

  // Condense large numbers: 3.1B, 1.5M, 2.56K
  const formatValue = (val: number): string => {
    const abs = Math.abs(val);
    if (abs >= 1e9) return `${(val / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
    if (abs >= 1e4) return `${(val / 1e3).toFixed(2)}K`;
    if (abs >= 1e3) return `${(val / 1e3).toFixed(1)}K`;
    if (Number.isInteger(val)) return val.toString();
    return val.toFixed(2);
  };

  // Trim trailing :00 seconds from time strings (15:34:00 → 15:34)
  const formatTimeTick = (ts: number): string => {
    const time = formatTimeOnly(new Date(ts * 1000), dateSettings);
    return time.replace(/:00$/, '').replace(/:00(\s)/, '$1'); // handle 24h and 12h formats
  };

  // Filter out all-zero series (no meaningful data)
  const activeLines = data && data.length > 0
    ? lines.filter((line) => data.some((point) => {
        const v = point[line.dataKey];
        return v !== undefined && v !== null && v !== 0;
      }))
    : lines;
  const hiddenCount = lines.length - activeLines.length;
  const hasMultipleLines = activeLines.length > 4;

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <div className="h-80 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <div className="h-80 flex items-center justify-center text-gray-500">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <div className="flex items-center gap-3">
          {hiddenCount > 0 && (
            <span className="text-xs text-gray-400">{hiddenCount} empty series hidden</span>
          )}
          {hasMultipleLines && (
            <button
              onClick={() => setLegendExpanded(!legendExpanded)}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <span>{legendExpanded ? 'Hide' : 'Show'} Legend ({activeLines.length} series)</span>
              {legendExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}
        </div>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="timestamp"
              tickFormatter={formatTimeTick}
              minTickGap={30}
            />
            <YAxis
              tickFormatter={(val) => unit ? `${formatValue(val)}${unit}` : formatValue(val)}
              width={65}
            />
            <Tooltip
              labelFormatter={(ts) => formatDate(new Date(ts * 1000), dateSettings)}
              formatter={(value: any, name: any) => [unit ? `${formatValue(Number(value))} ${unit}` : formatValue(Number(value)), String(name ?? '')] as [string, string]}
            />
            {legendExpanded && (
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="top"
                wrapperStyle={{
                  maxHeight: '100%',
                  overflowY: 'auto',
                  fontSize: '12px',
                  lineHeight: '20px',
                  paddingLeft: '20px',
                  width: 'auto',
                  maxWidth: '200px',
                }}
              />
            )}
            {activeLines.map((line) => (
              <Line
                key={line.dataKey}
                type="monotone"
                dataKey={line.dataKey}
                name={line.name}
                stroke={line.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

